import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Alert,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { FaPoundSign, FaExchangeAlt, FaSave, FaSync, FaHistory } from 'react-icons/fa';
import CurrencyDropdown from './currencyDropdown';
import { useCurrency } from './currencyContext.js';

const CurrencySettings = () => {
  const {
    currency,
    exchangeRate,
    updateCurrencySettings,
    refreshSettings,
    loading,
    error,
    clearError,
    fetchCurrentExchangeRate
  } = useCurrency();

  const [localCurrency, setLocalCurrency] = useState(currency);
  const [localExchangeRate, setLocalExchangeRate] = useState(exchangeRate.toString());
  const [autoUpdate, setAutoUpdate] = useState(true);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);

  // Sync local state with context
  useEffect(() => {
    setLocalCurrency(currency);
    setLocalExchangeRate(exchangeRate.toString());
  }, [currency, exchangeRate]);

  // Fetch current rate on component mount
  useEffect(() => {
    const initializeRate = async () => {
      await fetchCurrentExchangeRate();
    };
    initializeRate();
  }, []);

  const handleSaveSettings = async () => {
    setSaving(true);
    const result = await updateCurrencySettings(localCurrency, parseFloat(localExchangeRate));

    if (result.success) {
      setSaveSuccess(true);
      setShowSnackbar(true);
      setTimeout(() => {
        setSaveSuccess(false);
        setShowSnackbar(false);
      }, 3000);
    }
    setSaving(false);
  };

  const handleCurrencyChange = (newCurrency) => {
    setLocalCurrency(newCurrency);
  };

  const calculateReverseRate = () => {
    const rate = parseFloat(localExchangeRate);
    return rate > 0 ? (1 / rate).toFixed(6) : 0;
  };

  const handleRefresh = async () => {
    await refreshSettings();
  };

  const handleCloseSnackbar = () => {
    setShowSnackbar(false);
  };

  const handleCloseError = () => {
    clearError();
  };

  // Auto-fetch current rate when autoUpdate is true
  const handleAutoUpdateChange = (value) => {
    setAutoUpdate(value);
    if (value) {
      fetchCurrentExchangeRate();
    }
  };

  if (loading && !currency) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Success Alert */}
      <Snackbar
        open={showSnackbar}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={handleCloseSnackbar}>
          Currency settings saved successfully! All prices will now display in {localCurrency}.
        </Alert>
      </Snackbar>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={handleCloseError}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" color="primary" fontWeight="bold">
            Currency Settings
          </Typography>
          <Button
            variant="outlined"
            startIcon={<FaSync />}
            onClick={handleRefresh}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh Rates'}
          </Button>
        </Box>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Manage your base currency and exchange rate preferences for accurate financial calculations.
        </Typography>

        <Grid container spacing={4}>
          {/* Base Currency Selection */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FaPoundSign color="#004aad" />
                  Base Currency Selection
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Choose your primary currency for all transactions and reporting
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Select Base Currency:
                  </Typography>
                  <CurrencyDropdown
                    selectedCurrency={localCurrency}
                    onCurrencyChange={handleCurrencyChange}
                  />
                </Box>

                <Box sx={{ bgcolor: '#f8f9fa', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Current Selection:
                  </Typography>
                  <Typography variant="h6" color="primary">
                    {localCurrency === 'GBP' ? 'British Pound (GBP)' : 'Pakistani Rupee (PKR)'}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Exchange Rate Management */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FaExchangeAlt color="#28a745" />
                  Exchange Rate Settings
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Configure GBP to PKR exchange rate for accurate conversions
                </Typography>

                <Box sx={{ mb: 3 }}>
                  <TextField
                    fullWidth
                    label="GBP to PKR Exchange Rate"
                    type="number"
                    value={localExchangeRate}
                    onChange={(e) => setLocalExchangeRate(e.target.value)}
                    InputProps={{
                      startAdornment: <FaPoundSign style={{ marginRight: 8, color: '#666' }} />,
                    }}
                    helperText="1 GBP equals how many PKR?"
                    variant="outlined"
                    disabled={loading || autoUpdate}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <FormControl fullWidth disabled={loading}>
                    <InputLabel>Rate Update Method</InputLabel>
                    <Select
                      value={autoUpdate}
                      label="Rate Update Method"
                      onChange={(e) => handleAutoUpdateChange(e.target.value)}
                    >
                      <MenuItem value={true}>Automatic (Live Rates)</MenuItem>
                      <MenuItem value={false}>Manual Entry</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Rate Display */}
                <Box sx={{ bgcolor: '#e8f5e8', p: 2, borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Exchange Rate:
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                      <Typography variant="h6">1 GBP</Typography>
                    </Box>
                    <Typography variant="h6">=</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>

                      <Typography variant="h6">{parseFloat(localExchangeRate).toFixed(2)} PKR</Typography>
                    </Box>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Reverse: 1 PKR = {calculateReverseRate()} GBP
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Grid>

          {/* Current Settings Overview */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FaHistory color="#ff6b35" />
                  Settings Overview
                </Typography>
                <Divider sx={{ my: 2 }} />
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Base Currency
                    </Typography>
                    <Typography variant="h6">
                      {localCurrency === 'GBP' ? 'British Pound' : 'Pakistani Rupee'} ({localCurrency})
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Exchange Rate
                    </Typography>
                    <Typography variant="h6">
                      1 GBP = {parseFloat(localExchangeRate).toFixed(2)} PKR
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="body2" color="text.secondary">
                      Update Method
                    </Typography>
                    <Typography variant="h6">
                      {autoUpdate ? 'Automatic' : 'Manual'}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Save Button */}
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              setLocalCurrency('GBP');
              setLocalExchangeRate('370.08'); // Updated default
              setAutoUpdate(true);
            }}
            disabled={saving || loading}
          >
            Reset to Default
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={handleSaveSettings}
            sx={{ px: 4 }}
            startIcon={saving ? <CircularProgress size={20} /> : <FaSave />}
            disabled={saving || loading}
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>

      </Paper>
    </Container>
  );
};

export default CurrencySettings;